import React, { useState } from "react";
import { motion } from "framer-motion";
import { PackageCheck, Truck, ArrowRight } from "lucide-react";
import { AUDIENCE } from "@/constants/testIds";

const audiences = [
  {
    id: "shippers",
    icon: PackageCheck,
    title: "For Shippers",
    blurb:
      "You ship goods. We make sure they get there — on time, on budget, and without the supplier juggling.",
    points: [
      "One call, one contact, one invoice",
      "Vetted, compliant carriers — every load",
      "Scalable capacity across Southern Africa",
      "Backed by relationships, not portals",
    ],
    cta: "Get a quote for your next load",
    img: "https://images.unsplash.com/photo-1568438350562-2cae6d394ad0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    id: "carriers",
    icon: Truck,
    title: "For Carriers",
    blurb:
      "You run trucks. We bring you loads — backhauls included — so your wheels keep turning and your invoices keep moving.",
    points: [
      "Direct access to a wide volume of loads",
      "Fewer empty trips, quicker turnarounds",
      "Single customer relationship — that's us",
      "Reliable, on-time settlement",
    ],
    cta: "Apply to join the carrier network",
    img: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
];

export default function CustomerTypes() {
  const [active, setActive] = useState("shippers");
  const current = audiences.find((a) => a.id === active);
  const Icon = current.icon;

  return (
    <section
      id="carriers"
      data-testid={AUDIENCE.section}
      className="relative py-24 md:py-32 bg-[#002050] overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid-dark opacity-60" />
      <div className="absolute -top-24 -left-24 h-[420px] w-[420px] rounded-full bg-[#0070C0]/30 blur-3xl" />
      <div className="absolute -bottom-24 -right-24 h-[420px] w-[420px] rounded-full bg-[#00AEEF]/20 blur-3xl" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <div className="text-xs md:text-sm uppercase tracking-[0.24em] font-bold text-[#00AEEF]">
            Two sides. One brokerage.
          </div>
          <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white leading-[1.05]">
            Built for shippers.
            <span className="block text-[#00AEEF]">Loved by carriers.</span>
          </h2>
          <p className="mt-5 text-lg text-white/75 leading-relaxed">
            Whether you've got freight to move or a truck to fill, we sit
            between you and the other side — making both jobs easier.
          </p>
        </div>

        <div className="mt-12 flex flex-wrap gap-2">
          {audiences.map((a) => {
            const ATabIcon = a.icon;
            const isActive = a.id === active;
            return (
              <button
                key={a.id}
                onClick={() => setActive(a.id)}
                data-testid={AUDIENCE.card(a.id)}
                className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold transition-all duration-300 ${
                  isActive
                    ? "bg-white text-[#002050] shadow-[0_10px_30px_rgba(0,174,239,0.35)]"
                    : "bg-white/5 text-white/80 hover:bg-white/10 border border-white/10"
                }`}
              >
                <ATabIcon className="h-4 w-4" strokeWidth={2} />
                {a.title}
              </button>
            );
          })}
        </div>

        <motion.div
          key={current.id}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mt-10 grid lg:grid-cols-12 gap-8 lg:gap-12 items-stretch"
        >
          <div className="lg:col-span-7 rounded-3xl overflow-hidden relative border border-white/10 min-h-[380px]">
            <img
              src={current.img}
              alt={current.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#002050]/85 via-[#002050]/40 to-transparent" />
            <div className="relative h-full p-8 md:p-10 flex flex-col justify-end">
              <div className="inline-flex w-fit items-center gap-2 bg-white/10 border border-white/20 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.18em] text-white backdrop-blur-md">
                <Icon className="h-3.5 w-3.5" /> {current.title}
              </div>
              <h3 className="font-display mt-4 text-3xl md:text-4xl font-bold tracking-tight text-white">
                {current.title}
              </h3>
              <p className="mt-3 max-w-xl text-white/85 text-lg leading-relaxed">
                {current.blurb}
              </p>
            </div>
          </div>

          <div className="lg:col-span-5 rounded-3xl bg-white/5 border border-white/10 backdrop-blur-md p-8 md:p-10 flex flex-col">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#00AEEF]">
              What you get
            </div>
            <ul className="mt-5 space-y-4 flex-1">
              {current.points.map((p, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-white/90 text-[15px]"
                >
                  <span className="mt-1 h-2 w-2 rounded-full bg-[#00AEEF] shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
            <a
              href="#quote"
              className="mt-8 inline-flex items-center justify-center gap-2 bg-white text-[#002050] hover:bg-[#00AEEF] hover:text-white px-6 py-3.5 rounded-xl font-bold text-sm transition-colors group"
            >
              {current.cta}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
